import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import type {WebCommitInfo} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {repositoryTreePath} from '@github-ui/paths'
import {Link} from '@github-ui/react-router'
import type {WebCommitDialogState} from '@github-ui/web-commit-dialog'
import {Button} from '@primer/react'
import {lazy, startTransition, Suspense, useRef, useState} from 'react'

import styles from './DeleteHeaderButtons.module.css'

const WebCommitDialog = lazy(() => import('../../blob-edit/WebCommitDialog'))

export function DeleteHeaderButtons({webCommitInfo, isBlob}: {webCommitInfo: WebCommitInfo; isBlob: boolean}) {
  const [webCommitDialogState, setWebCommitDialogState] = useState<WebCommitDialogState>('closed')
  const commitChangesOpen = webCommitDialogState === 'pending' || webCommitDialogState === 'saving'

  const returnFocusRef = useRef<HTMLButtonElement>(null)
  const repo = useCurrentRepository()
  const path = usePath()
  const refInfo = useRefInfo()
  const {helpUrl} = useReposAppPayload()

  const cancelUrl = repositoryTreePath({repo, commitish: refInfo.name, action: isBlob ? 'blob' : 'tree', path})

  if (webCommitInfo.shouldFork || webCommitInfo.shouldUpdate || webCommitInfo.lockedOnMigration) {
    return null
  }

  return (
    <div className={styles.Box}>
      <Button as={Link} to={cancelUrl}>
        Cancel changes
      </Button>
      <Button
        variant="primary"
        ref={returnFocusRef}
        onClick={() => {
          startTransition(() => {
            setWebCommitDialogState('pending')
          })
        }}
      >
        Commit changes...
      </Button>
      {commitChangesOpen && (
        <Suspense fallback={null}>
          <WebCommitDialog
            isNewFile={false}
            isDelete
            helpUrl={helpUrl}
            ownerName={repo.ownerLogin}
            dialogState={webCommitDialogState}
            setDialogState={setWebCommitDialogState}
            refName={refInfo.name}
            placeholderMessage={`Delete ${path}${isBlob ? '' : ' directory'}`}
            webCommitInfo={webCommitInfo}
            returnFocusRef={returnFocusRef}
          />
        </Suspense>
      )}
    </div>
  )
}
